import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    mode: 'light',

    primary: {
      main: '#667eea', // Electric blue
      light: '#8b9ff5',
      dark: '#764ba2', // Vibrant purple
    },
    secondary: {
      main: '#764ba2', // Vibrant purple
      light: '#9b6fc4',
      dark: '#5a3580',
    },
    navy: {
      main: '#142C5C',
      light: '#1e3a7a',
      dark: '#0d1f42',
      contrastText: '#ffffff',
    },
    neutral: {
      main: '#ffffff',
      contrastText: '#1a1a2e',
      dark: '#f8f9ff',
    },
    error: {
      main: '#ff6b6b', // Coral
      light: '#ff8e8e',
      dark: '#e55555',
    },
    success: {
      main: '#20c997', // Teal
      light: '#4dd9b4',
      dark: '#17a87d',
    },
    warning: {
      main: '#ffc107', // Amber
      light: '#ffcd38',
      dark: '#e0a800',
    },
    info: {
      main: '#667eea',
      light: '#8b9ff5',
      dark: '#4c64d4',
    },
    text: {
      main: '#1a1a2e',
      primary: '#1a1a2e',
      secondary: '#64748B',
    },
    background: {
      default: '#f0f2ff',
      paper: '#ffffff',
    },
    custom: {
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      coral: '#ff6b6b',
      teal: '#20c997',
      amber: '#ffc107',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(102, 126, 234, 0.12)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        // On phones, let every dialog use (almost) the full viewport width
        // instead of MUI's default 32px margin on each side.
        paper: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            margin: 12,
            width: 'calc(100% - 24px)',
            maxWidth: 'calc(100% - 24px)',
            maxHeight: 'calc(100% - 24px)',
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            padding: '12px 16px',
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            padding: '14px 16px',
          },
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            padding: '8px 16px 14px',
            flexWrap: 'wrap',
          },
        }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        // Wide tables scroll horizontally inside their container rather than
        // blowing out the page width on narrow screens.
        root: {
          overflowX: 'auto',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
  },
  MuiSlider: {
    root: {
      color: '#667eea',
    },
    thumb: {
      backgroundColor: '#667eea',
    },
    track: {
      color: '#667eea',
    },
    rail: {
      color: '#e0e0e0',
    },
  },
  typography: {
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    allVariants: {
      color: '#1a1a2e',
    },
    fontFamily: ['Montserrat', 'sans-serif'].join(','),
    fontSize: 16,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '54px',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '36px',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '28px',
      fontWeight: 700,
    },
    h4: {
      fontSize: '24px',
      fontWeight: 700,
    },
    h5: {
      fontSize: '18px',
      fontWeight: 700,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
    },
    body1: {
      fontSize: '14px',
      fontWeight: 500,
    },
    body2: {
      fontSize: '12px',
      fontWeight: 400,
    },
    subtitle1: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#64748B',
    },
    subtitle2: {
      fontSize: '12px',
      fontWeight: 500,
      color: '#64748B',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(102, 126, 234, 0.08)',
    '0 4px 16px rgba(102, 126, 234, 0.12)',
    '0 6px 24px rgba(102, 126, 234, 0.16)',
    '0 8px 32px rgba(102, 126, 234, 0.20)',
    '0 12px 40px rgba(102, 126, 234, 0.24)',
    '0 16px 48px rgba(102, 126, 234, 0.28)',
    '0 20px 56px rgba(102, 126, 234, 0.32)',
    '0 24px 64px rgba(102, 126, 234, 0.36)',
    ...Array(16).fill('0 24px 64px rgba(102, 126, 234, 0.36)'),
  ],
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});
